import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listSessions, type SessionSummary } from '../api/client';

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function computeDuration(createdAt: string, status: string, updatedAt?: string): string {
  const start = new Date(createdAt).getTime();
  const end = (status === 'completed' && updatedAt) ? new Date(updatedAt).getTime() : Date.now();
  const diffMs = end - start;
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.floor((diffMs % 3_600_000) / 60_000);
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m`;
}

function StatusBadge({ status, reason }: { status: SessionSummary['status']; reason?: string | null }) {
  const classMap: Record<string, string> = {
    active: 'badge badge--green',
    paused: 'badge badge--yellow',
    completed: 'badge badge--gray',
  };
  const label = status === 'completed' && reason === 'idle_timeout'
    ? 'Timed out'
    : status === 'completed' && reason === 'manual'
    ? 'Completed'
    : status;
  return <span className={classMap[status] || 'badge'} title={reason ?? undefined}>{label}</span>;
}

export function SessionsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sessions', page],
    queryFn: () => listSessions(page, pageSize),
  });

  const sessions = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="page">
      <h2>Sessions</h2>
      <p className="page-subtitle">
        Monitor active and completed agent sessions
      </p>

      {isLoading && <div className="loading-state">Loading sessions...</div>}

      {isError && (
        <div className="error-state">
          Failed to load sessions: {(error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {!isLoading && !isError && sessions.length === 0 && (
        <div className="empty-state">No sessions found.</div>
      )}

      {!isLoading && !isError && sessions.length > 0 && (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Creation Time</th>
                  <th>Simulated Time</th>
                  <th>Status</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr
                    key={session.id}
                    className="data-table-row--clickable"
                    onClick={() => navigate(`/sessions/${session.id}`)}
                  >
                    <td className="cell-mono">{session.agentId || 'N/A'}</td>
                    <td>{formatDateTime(session.createdAt)}</td>
                    <td>{formatDateTime(session.simulatedTime)}</td>
                    <td>
                      <StatusBadge status={session.status} reason={session.completionReason} />
                    </td>
                    <td>{computeDuration(session.createdAt, session.status, session.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page} of {totalPages} ({total} total)
            </span>
            <button
              className="pagination-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
